/**
 * ============================================================================
 * WINDOW COMPONENT
 * ============================================================================
 *
 * A macOS-style window component with full drag, resize, and snap support.
 * Supports two modes:
 * - "app": Renders a React component inside the window
 * - "browser": Renders an iframe with browser-style chrome
 *
 * @packageDocumentation
 */

"use client";

import {
  Component,
  ComponentType,
  type ErrorInfo,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useDesktopContext } from "../contexts/DesktopContext";
import { getActiveDragPayload } from "./shared/dragProtocol";
import { useNotifications } from "../contexts/NotificationContext";
import { darkTheme, useThemeOptional } from "../contexts/ThemeContext";
import { ActivityTrackingProvider } from "../hooks/useActivityTracking";
import {
  hardReloadApp,
  handleStaleChunk,
  isStaleChunkError,
} from "../utils/staleChunkRecovery";
import type {
  AppWindowConfig,
  BrowserWindowConfig,
  WindowActions,
  WindowConfig,
  WindowPosition,
  WindowState,
} from "../types";
import {
  isAppWindow,
  isBrowserWindow,
  isWindowMinimized,
  isWindowOpen,
  resolveDimensionPercentage,
} from "../types";
import type { DimensionPercentage } from "../types";

/* ==========================================================================
   TYPES
   ========================================================================== */

export interface WindowProps {
  /** Window configuration */
  config: WindowConfig;
  /** Current window state */
  state: WindowState;
  /** Window control actions */
  actions: WindowActions;
  /** Starting position for open animation (usually icon position) */
  iconPosition?: { x: number; y: number };
  /** Get current icon position for minimize animation */
  getIconPosition?: () => { x: number; y: number };
  /** Whether the device is mobile */
  isMobile?: boolean;
  /** Whether the virtual keyboard is currently open (touch devices) */
  keyboardOpen?: boolean;
  /** Height of the dock (if present) to avoid overlapping */
  dockHeight?: number;
  /** Effective menu bar height (0 when auto-hidden) */
  menuBarHeight?: number;
  /** Initial X position (overrides config) */
  initialX?: number;
  /** Initial Y position (overrides config) */
  initialY?: number;
  /** Initial width (overrides config) */
  width?: string;
  /** Initial height (overrides config) */
  height?: string;
  /** Keep component mounted when minimized (e.g., for background audio) */
  keepMounted?: boolean;
  /**
   * Viewport-space rect of the App Switcher slot this window should preview
   * into. When set and the window is minimized, Window renders the live
   * content (via a STABLE portal into document.body) and positions it with
   * position:fixed over this rect. Using a stable portal target — rather
   * than portaling directly into the slot element — prevents React from
   * reparenting the subtree on every switcher open/close cycle, which was
   * causing intermittent paint failures (canvas, iframe, and timing-race
   * mounts). The parent listens for scroll/resize and republishes on
   * change so position stays locked to the slot.
   */
  previewRect?: { x: number; y: number; width: number; height: number };
  /** Persisted geometry from localStorage */
  persistedGeometry?: {
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    savedPosition?: { x: number; y: number };
    savedSize?: { width: number; height: number };
    previousGeometry?: { x: number; y: number; width: number; height: number };
    isSnapped?: "left" | "right" | null;
  };
  /** Callback when geometry changes */
  onGeometryChange?: (geometry: {
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    savedPosition?: { x: number; y: number };
    savedSize?: { width: number; height: number };
    previousGeometry?: { x: number; y: number; width: number; height: number };
    isSnapped?: "left" | "right" | null;
  }) => void;
}

type ResizeDirection =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top"
  | "right"
  | "bottom"
  | "left"
  | null;

/* ==========================================================================
   HELPERS
   ========================================================================== */

/**
 * Parse a CSS size value (px, vw, vh) to pixels.
 */
function parseSize(size: string): number {
  if (typeof window === "undefined") return parseFloat(size);
  if (size.endsWith("vw")) {
    return (parseFloat(size) / 100) * window.innerWidth;
  } else if (size.endsWith("vh")) {
    return (parseFloat(size) / 100) * window.innerHeight;
  }
  return parseFloat(size);
}

/**
 * Default dimensions for app windows.
 */
const DEFAULT_APP_DIMENSIONS = {
  width: "650px",
  height: "500px",
  minWidth: 400,
  minHeight: 300,
};

/**
 * Default dimensions for browser windows.
 */
const DEFAULT_BROWSER_DIMENSIONS = {
  width: "1000px",
  height: "700px",
  minWidth: 500,
  minHeight: 400,
};

/**
 * Cascade offset for each new window.
 */
const CASCADE_OFFSET = 56;

/**
 * Padding from screen edges for positioned windows.
 */
const EDGE_PADDING = 40;

/**
 * Maximum number of cascade steps before wrapping back to the start.
 */
const MAX_CASCADE_STEPS = 10;

/**
 * Clamp a window's size and position so it is fully visible within the current viewport.
 * Shrinks size first (respecting minWidth/minHeight), then adjusts position.
 */
function clampToViewport(
  x: number,
  y: number,
  w: number,
  h: number,
  minW: number,
  minH: number,
  dockHeight: number = 0
): { x: number; y: number; width: number; height: number } {
  if (typeof window === "undefined") return { x, y, width: w, height: h };

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Preserve the requested dimensions — only enforce minimums and a
  // viewport-overall ceiling. Tall windows keep their height and get
  // shifted up below, instead of being shrunk to fit the dock.
  const width = Math.max(minW, Math.min(w, vw));
  const height = Math.max(minH, Math.min(h, vh));

  // Position so the window doesn't sit under the dock. If the window is
  // taller than the available space (menu bar → dock), pin it directly
  // below the menu bar — bottom may extend behind the dock, but the
  // title bar stays reachable.
  const maxY = vh - dockHeight - height;
  const cy =
    maxY >= MENU_BAR_HEIGHT
      ? Math.max(MENU_BAR_HEIGHT, Math.min(y, maxY))
      : MENU_BAR_HEIGHT;

  // Keep the window fully on-screen horizontally; preserve user-dragged
  // offsets within those bounds.
  const maxX = Math.max(0, vw - width);
  const cx = Math.max(0, Math.min(x, maxX));

  return { x: cx, y: cy, width, height };
}

/**
 * Height of the menu bar at the top of the screen.
 */
const MENU_BAR_HEIGHT = 28;

/**
 * Calculate x,y position from a named position slot.
 * Returns coordinates that respect screen bounds and window size.
 */
function calculatePositionFromSlot(
  position: WindowPosition,
  windowWidth: number,
  windowHeight: number,
  cascadeOffset: number = 0,
  dockHeight: number = 0
): { x: number; y: number } {
  if (typeof window === "undefined") {
    return { x: 100 + cascadeOffset, y: 50 + cascadeOffset };
  }

  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  const availableHeight = screenHeight - MENU_BAR_HEIGHT - dockHeight;

  // Calculate base positions (account for menu bar at top and dock at bottom)
  const centerX = (screenWidth - windowWidth) / 2;
  const centerY = MENU_BAR_HEIGHT + (availableHeight - windowHeight) / 2; // Center in available space between menu bar and dock
  const leftX = EDGE_PADDING;
  const rightX = screenWidth - windowWidth - EDGE_PADDING;
  const topY = MENU_BAR_HEIGHT + 12; // Just below menu bar with small gap
  const bottomY = screenHeight - windowHeight - EDGE_PADDING - dockHeight;

  let x: number;
  let y: number;

  switch (position) {
    case "center":
      x = centerX;
      y = centerY;
      break;
    case "top-left":
      x = leftX;
      y = topY;
      break;
    case "top-right":
      x = rightX;
      y = topY;
      break;
    case "bottom-left":
      x = leftX;
      y = bottomY;
      break;
    case "bottom-right":
      x = rightX;
      y = bottomY;
      break;
    case "top-center":
      x = centerX;
      y = topY;
      break;
    case "bottom-center":
      x = centerX;
      y = bottomY;
      break;
    case "left-center":
      x = leftX;
      y = centerY;
      break;
    case "right-center":
      x = rightX;
      y = centerY;
      break;
    default:
      x = centerX;
      y = centerY;
  }

  // Apply cascade offset
  x += cascadeOffset;
  y += cascadeOffset;

  // Keep the full window on-screen with breathing room from the dock and
  // the right edge. Cascaded center windows can otherwise drift bottom-right
  // far enough to overlap the dock or get clipped at the viewport edge.
  const maxX = screenWidth - windowWidth - EDGE_PADDING;
  const maxY = screenHeight - dockHeight - windowHeight - EDGE_PADDING;
  x =
    maxX >= EDGE_PADDING
      ? Math.max(EDGE_PADDING, Math.min(x, maxX))
      : Math.max(0, Math.min(x, Math.max(0, screenWidth - windowWidth)));
  y =
    maxY >= MENU_BAR_HEIGHT
      ? Math.max(MENU_BAR_HEIGHT, Math.min(y, maxY))
      : MENU_BAR_HEIGHT;

  return { x, y };
}

/* ==========================================================================
   COMPONENT
   ========================================================================== */

/**
 * A fully-featured macOS-style window component.
 *
 * @remarks
 * Features:
 * - Drag from title bar
 * - Resize from edges and corners
 * - Window snap zones (top = maximize, sides = split)
 * - Minimize/maximize animations
 * - Touch support for mobile
 * - Two rendering modes: app (component) and browser (iframe)
 *
 * @example
 * ```tsx
 * <Window
 *   config={windowConfig}
 *   state={windowState}
 *   actions={windowActions}
 *   isMobile={false}
 * />
 * ```
 */
export function Window({
  config,
  state,
  actions,
  iconPosition,
  getIconPosition,
  isMobile = false,
  keyboardOpen = false,
  dockHeight = 0,
  menuBarHeight: propMenuBarHeight,
  initialX: propInitialX,
  initialY: propInitialY,
  width: propWidth,
  height: propHeight,
  keepMounted = false,
  previewRect,
  persistedGeometry,
  onGeometryChange,
}: WindowProps) {
  // Cascade slot allocator from context. Acquire on closed→open transition,
  // release on close — see useLayoutEffect below.
  const { acquireCascadeSlot, releaseCascadeSlot } = useDesktopContext();
  const { sendNotification } = useNotifications();

  // Pick defaults based on window type
  const isBrowserType = isBrowserWindow(config);
  const defaults = isBrowserType
    ? DEFAULT_BROWSER_DIMENSIONS
    : DEFAULT_APP_DIMENSIONS;

  // Extract dimensions from config if available (only fixed dimensions have these fields)
  const configDimensions = config.dimensions;
  const isFixedDims = configDimensions && !configDimensions.responsive;
  const configPosition = isFixedDims ? configDimensions.position : undefined;
  const configInitialX = isFixedDims ? configDimensions.initialX : undefined;
  const configInitialY = isFixedDims ? configDimensions.initialY : undefined;
  const configWidth = isFixedDims ? configDimensions.width : undefined;
  const configHeight = isFixedDims ? configDimensions.height : undefined;
  // Percentage dimensions (only honored on desktop). Mobile auto-maximizes.
  const configDimensionPercentage: DimensionPercentage | undefined =
    isFixedDims && !isMobile ? configDimensions.dimensionPercentage : undefined;

  // Resolve percentage → pixel size against the available viewport (minus
  // menu bar + dock). Recomputed on every render so opening at the current
  // viewport always matches the configured ratio.
  const percentagePixels = (() => {
    if (!configDimensionPercentage) return null;
    if (typeof window === "undefined") return null;
    const availW = window.innerWidth;
    const availH = Math.max(
      0,
      window.innerHeight - MENU_BAR_HEIGHT - dockHeight
    );
    return resolveDimensionPercentage(
      configDimensionPercentage,
      availW,
      availH
    );
  })();

  // Props override config, which overrides defaults.
  // Percentage dimensions take precedence over width/height strings when set.
  const width =
    propWidth ??
    (percentagePixels ? `${percentagePixels.width}px` : undefined) ??
    configWidth ??
    defaults.width;
  const height =
    propHeight ??
    (percentagePixels ? `${percentagePixels.height}px` : undefined) ??
    configHeight ??
    defaults.height;

  // Determine position slot (defaults to 'center')
  const positionSlot: WindowPosition = configPosition ?? "center";

  // Cascade index is allocated lazily on the closed→open transition (see the
  // useLayoutEffect below). Allocating during render fires once per mounted
  // <Window>, including the dozens that mount closed at boot — so the very
  // first window the user opened was almost never index 0 (i.e. never truly
  // centered).
  const cascadeIndexRef = useRef<number | null>(null);
  const cascadeOffset =
    ((cascadeIndexRef.current ?? 0) % MAX_CASCADE_STEPS) * CASCADE_OFFSET;

  // Calculate position: explicit x/y overrides position slot, which overrides center default
  const getCalculatedPosition = () => {
    const windowWidth = parseSize(width);
    const windowHeight = parseSize(height);

    // If explicit x/y provided (via props or config), use them directly (no cascade)
    if (propInitialX !== undefined || propInitialY !== undefined) {
      return {
        x: propInitialX ?? 100,
        y: propInitialY ?? 50,
      };
    }

    if (configInitialX !== undefined || configInitialY !== undefined) {
      return {
        x: configInitialX ?? 100,
        y: configInitialY ?? 50,
      };
    }

    return calculatePositionFromSlot(
      positionSlot,
      windowWidth,
      windowHeight,
      cascadeOffset,
      dockHeight
    );
  };

  const calculatedPosition = getCalculatedPosition();
  const defaultX = calculatedPosition.x;
  const defaultY = calculatedPosition.y;
  const defaultWidth = width;
  const defaultHeight = height;
  // Per-window mins from config override the type-default mins so apps can
  // raise the drag-resize floor when their internal layout has a hard
  // minimum size below which content stops being usable.
  const staticMinWidth =
    (isFixedDims ? configDimensions.minWidth : undefined) ?? defaults.minWidth;
  const minHeight =
    (isFixedDims ? configDimensions.minHeight : undefined) ??
    defaults.minHeight;
  // Dynamic extra min-width: apps can raise their resize floor at
  // runtime by dispatching `window:<id>:min-width` with `{ extra }`.
  // Used by apps whose internal layout (e.g. expandable sidebar) needs
  // more horizontal room than the static config min when extra chrome
  // is visible. Effective min = static config min + dynamic extra.
  const [extraMinWidth, setExtraMinWidth] = useState<number>(0);
  useEffect(() => {
    const handler = ((e: CustomEvent<{ extra: number }>) => {
      setExtraMinWidth(Math.max(0, e.detail?.extra ?? 0));
    }) as EventListener;
    window.addEventListener(`window:${config.id}:min-width`, handler);
    return () =>
      window.removeEventListener(`window:${config.id}:min-width`, handler);
  }, [config.id]);
  const minWidth = staticMinWidth + extraMinWidth;
  // Auto-grow the window when the effective min jumps above current
  // width (e.g. user expands a sidebar that the static config min
  // didn't account for). Only grows; never shrinks the user's choice.
  useEffect(() => {
    setSize((prev) =>
      prev.width < minWidth ? { ...prev, width: minWidth } : prev
    );
  }, [minWidth]);

  const canMinimize = config.canMinimize !== false;
  const canMaximize = config.canMaximize !== false;
  const canResize = config.canResize !== false && !isMobile;
  const [dynamicHideTitleBar, setDynamicHideTitleBar] = useState(false);

  useEffect(() => {
    const handler = ((e: CustomEvent<{ hidden: boolean }>) => {
      setDynamicHideTitleBar(e.detail.hidden);
    }) as EventListener;
    window.addEventListener(`window:${config.id}:titlebar`, handler);
    return () =>
      window.removeEventListener(`window:${config.id}:titlebar`, handler);
  }, [config.id]);

  const hideTitleBar = config.hideTitleBar === true || dynamicHideTitleBar;

  // Check if we have icon position for animations
  const hasIconAnimation = iconPosition !== undefined;

  // Effective menu bar height — uses prop if provided (allows 0 when auto-hidden)
  const effectiveMenuBarHeight = propMenuBarHeight ?? MENU_BAR_HEIGHT;

  // On mobile/small screens, always open maximized
  // On desktop, respect the openMaximized config OR restored state from persistence
  const shouldOpenMaximized =
    isMobile ||
    config.openMaximized === true ||
    (isWindowOpen(state) && state.isMaximized);

  /* ========================================================================
     STATE
     ======================================================================== */

  // Compute viewport-clamped initial geometry (skip for animations & maximized)
  const clampedInitial = (() => {
    if (hasIconAnimation || shouldOpenMaximized) return null;
    const rawX = persistedGeometry?.position?.x ?? defaultX;
    const rawY = persistedGeometry?.position?.y ?? defaultY;
    const rawW = persistedGeometry?.size?.width ?? parseSize(defaultWidth);
    const rawH = persistedGeometry?.size?.height ?? parseSize(defaultHeight);
    return clampToViewport(
      rawX,
      rawY,
      rawW,
      rawH,
      minWidth,
      minHeight,
      dockHeight
    );
  })();

  const [position, setPosition] = useState(() => {
    if (hasIconAnimation) return { x: iconPosition.x, y: iconPosition.y };
    if (shouldOpenMaximized) return { x: 0, y: effectiveMenuBarHeight };
    return { x: clampedInitial!.x, y: clampedInitial!.y };
  });

  const [size, setSize] = useState(() => {
    if (hasIconAnimation) return { width: 60, height: 60 };
    if (shouldOpenMaximized && typeof window !== "undefined") {
      return {
        width: window.innerWidth,
        height: window.innerHeight - effectiveMenuBarHeight,
      };
    }
    return { width: clampedInitial!.width, height: clampedInitial!.height };
  });

  // Lazy cascade allocation on closed→open transition. Allocating during
  // render fired once per <Window> mount — including the dozens that mount
  // closed at boot — so the first window the user opened was almost never
  // index 0. Allocating on the actual open transition guarantees that the
  // first user-opened window with no other windows on screen is centered.
  const wasOpenRef = useRef(false);
  useLayoutEffect(() => {
    const isOpenNow = isWindowOpen(state);
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpenNow;

    if (isOpenNow && !wasOpen) {
      const skipCascade =
        positionSlot !== "center" ||
        hasIconAnimation ||
        shouldOpenMaximized ||
        persistedGeometry?.position !== undefined ||
        propInitialX !== undefined ||
        propInitialY !== undefined ||
        configInitialX !== undefined ||
        configInitialY !== undefined;
      if (skipCascade) return;

      const idx = acquireCascadeSlot(config.id);
      cascadeIndexRef.current = idx;
      const offset = (idx % MAX_CASCADE_STEPS) * CASCADE_OFFSET;
      const newPos = calculatePositionFromSlot(
        positionSlot,
        parseSize(width),
        parseSize(height),
        offset,
        dockHeight
      );
      setPosition(newPos);
    } else if (!isOpenNow && wasOpen) {
      // Free the slot so the next opening window can land on this position.
      releaseCascadeSlot(config.id);
      cascadeIndexRef.current = null;
    }
    // We intentionally only re-run on open-state changes; width/height/dock
    // changes during an active session shouldn't re-cascade an already-placed
    // window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWindowOpen(state)]);

  // Release any held cascade slot if the Window unmounts entirely (e.g.,
  // app uninstall removes it from the registry). Otherwise the slot would
  // leak and successive opens would skip past it indefinitely.
  useEffect(() => {
    return () => {
      releaseCascadeSlot(config.id);
    };
  }, [config.id, releaseCascadeSlot]);

  const [isDragging, setIsDragging] = useState(false);
  const [showTrafficOverlay, setShowTrafficOverlay] = useState(false);
  const [isResizing, setIsResizing] = useState<ResizeDirection>(null);
  const [isMaximized, setIsMaximized] = useState(shouldOpenMaximized);
  // Use persisted snap state if available
  const [isSnapped, setIsSnapped] = useState<"left" | "right" | null>(
    persistedGeometry?.isSnapped ?? null
  );
  const [snapZone, setSnapZone] = useState<"top" | "left" | "right" | null>(
    null
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isOpening, setIsOpening] = useState(hasIconAnimation);
  const [isClosing, setIsClosing] = useState(false);
  const [opacity, setOpacity] = useState(hasIconAnimation ? 0 : 1);

  // Browser window states
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState<string | null>(null);

  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const dragOverCountRef = useRef(0);

  // Tracks whether a desktop-file drag is in flight anywhere on the
  // page. When true, inactive windows must drop their click-activation
  // overlay so the drag can reach app-level drop targets underneath.
  const [isFileDragInFlight, setIsFileDragInFlight] = useState(false);
  useEffect(() => {
    const onStart = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("application/x-desktop-file")) {
        setIsFileDragInFlight(true);
      }
    };
    const onEnd = () => setIsFileDragInFlight(false);
    window.addEventListener("dragstart", onStart);
    window.addEventListener("dragend", onEnd);
    window.addEventListener("drop", onEnd);
    return () => {
      window.removeEventListener("dragstart", onStart);
      window.removeEventListener("dragend", onEnd);
      window.removeEventListener("drop", onEnd);
    };
  }, []);

  /* ========================================================================
     REFS
     ======================================================================== */

  const isAnimating = useRef(false);
  /**
   * Set true when the minimize animation completes (window has shrunk to
   * the dock icon with opacity 0 and state has flipped to `minimized`).
   * Consumed by the restore-from-minimize effect to detect that the next
   * un-minimize transition needs to run the reverse animation. We used
   * to piggyback on `isClosing` for this, but `isClosing` has to be
   * CLEARED to let `hiddenMounted` (and therefore the App Switcher
   * preview) engage — so the two roles now have separate flags.
   */
  const needsRestoreAnimation = useRef(false);
  const savedPosition = useRef(
    persistedGeometry?.savedPosition ?? { x: defaultX, y: defaultY }
  );
  const savedSize = useRef(
    persistedGeometry?.savedSize ?? {
      width: parseSize(defaultWidth),
      height: parseSize(defaultHeight),
    }
  );
  const previousStateRef = useRef(
    persistedGeometry?.previousGeometry ?? {
      x: defaultX,
      y: defaultY,
      width: parseSize(defaultWidth),
      height: parseSize(defaultHeight),
    }
  );
  // Tracks whether the user has manually resized/maximized this window or
  // had a persisted size on open. Once true, `dimensionPercentage` auto-
  // tracking stops — the user's size always wins.
  const userResizedRef = useRef<boolean>(Boolean(persistedGeometry?.size));
  // Latest committed position — read by the percentage-tracking effect
  // without re-binding the resize listener on every drag tick.
  const latestPositionRef = useRef({ x: defaultX, y: defaultY });
  latestPositionRef.current = position;
  const stoplightRef = useRef<HTMLDivElement>(null);
  const dragClosedRef = useRef(false);
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    needsRestore: false,
    initialClientX: 0,
    initialClientY: 0,
    startTime: 0,
  });
  const resizeRef = useRef({
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    startPosX: 0,
    startPosY: 0,
  });
  const iframeRef = useRef<HTMLIFrameElement>(null);

  /* ========================================================================
     DERIVED STATE
     ======================================================================== */

  const isOpen = isWindowOpen(state);
  const isMinimized = isWindowMinimized(state);
  const isActive = isOpen && state.isActive;
  const zIndex = isOpen ? state.zIndex : isMinimized ? state.zIndex : 0;

  // Theme support (falls back to dark theme if no provider)
  const themeContext = useThemeOptional();
  const colors = themeContext?.colors ?? darkTheme;

  // Reset position/size when reopening after a drag-to-close
  const prevOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current && dragClosedRef.current) {
      dragClosedRef.current = false;
      // Reset to fullscreen on mobile, default position on desktop
      if (isMobile) {
        setPosition({ x: 0, y: effectiveMenuBarHeight });
        setSize({
          width: window.innerWidth,
          height: window.innerHeight - effectiveMenuBarHeight,
        });
        setIsMaximized(true);
      } else {
        setPosition({ x: defaultX, y: defaultY });
        setSize({
          width: parseSize(defaultWidth),
          height: parseSize(defaultHeight),
        });
        setIsMaximized(false);
      }
      setIsSnapped(null);
    }
    prevOpenRef.current = isOpen;
  }, [
    isOpen,
    isMobile,
    effectiveMenuBarHeight,
    defaultX,
    defaultY,
    defaultWidth,
    defaultHeight,
  ]);

  /* ========================================================================
     GEOMETRY PERSISTENCE
     ======================================================================== */

  // Use a ref for the callback to avoid re-triggering the effect
  const onGeometryChangeRef = useRef(onGeometryChange);
  onGeometryChangeRef.current = onGeometryChange;

  // Track if this is the initial mount to skip first save
  const isInitialMount = useRef(true);

  // Persist geometry changes (debounced via the window manager)
  useEffect(() => {
    // Skip the initial mount - we don't want to save on first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Don't persist during animations, viewport resizing, or when not open
    if (isOpening || isClosing || isTransitioning || !isOpen) return;
    if (viewportResizingRef.current) return;
    // Restore-from-minimize: when the window flips back to open, state
    // initially shows the minimize-animation values (icon position,
    // 60×60, opacity 0). The restore effect will immediately overwrite
    // those with savedPosition/savedSize, but this persist effect runs
    // first in the same commit — skipping here avoids briefly persisting
    // the bad values and breaking geometry across refreshes.
    if (needsRestoreAnimation.current) return;

    onGeometryChangeRef.current?.({
      position,
      size,
      savedPosition: savedPosition.current,
      savedSize: savedSize.current,
      previousGeometry: previousStateRef.current,
      isSnapped,
    });
  }, [
    position,
    size,
    isSnapped,
    isOpen,
    isOpening,
    isClosing,
    isTransitioning,
  ]);

  /* ========================================================================
     WINDOW ACTIONS
     ======================================================================== */

  const handleClose = useCallback(() => {
    actions.close();
  }, [actions]);

  // Listen for drag-to-close events from the WindowDragCloseTarget
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail.windowId === config.id) {
        dragClosedRef.current = true;
        actions.close();
      }
    };
    window.addEventListener("window:drag-close", handler);
    return () => window.removeEventListener("window:drag-close", handler);
  }, [config.id, actions]);

  const handleShare = useCallback(() => {
    const url = new URL(window.location.href);
    // Use path-based deep link — survives WhatsApp/iMessage/social app redirects
    url.pathname = `/app/${encodeURIComponent(config.id)}`;
    url.search = "";
    url.hash = "";
    navigator.clipboard.writeText(url.toString()).then(() => {
      sendNotification({
        title: "Link copied",
        message: `Share link for ${config.title} copied to clipboard`,
        type: "success",
        duration: 2000,
      });
    });
  }, [config.id, config.title, sendNotification]);

  const handleMinimize = useCallback(() => {
    if (isAnimating.current || !canMinimize) return;

    const currentIconPos = getIconPosition?.() ?? iconPosition;

    if (currentIconPos) {
      isAnimating.current = true;
      savedPosition.current = { x: position.x, y: position.y };
      savedSize.current = { width: size.width, height: size.height };

      setIsClosing(true);
      setIsTransitioning(true);
      setPosition(currentIconPos);
      setSize({ width: 60, height: 60 });
      setOpacity(0);

      setTimeout(() => {
        isAnimating.current = false;
        // Clear the animation flag BEFORE flipping to minimized, so the
        // render that sees isMinimized=true also sees isClosing=false.
        // Otherwise hiddenMounted stays false forever and the App Switcher
        // preview gate is never opened (mid-session bug that required a
        // full refresh to reset).
        setIsClosing(false);
        setIsTransitioning(false);
        // Mark that the NEXT un-minimize transition needs the reverse
        // animation — the restore effect below reads this flag instead
        // of isClosing, which used to double as this signal.
        needsRestoreAnimation.current = true;
        actions.minimize();
      }, 300);
    } else {
      actions.minimize();
    }
  }, [actions, canMinimize, getIconPosition, iconPosition, position, size]);

  const handleMaximize = useCallback(() => {
    if (!canMaximize) return;

    // User-initiated size change — stop auto-tracking percentage dimensions.
    userResizedRef.current = true;
    setIsTransitioning(true);

    if (isMaximized) {
      setPosition({
        x: previousStateRef.current.x,
        y: previousStateRef.current.y,
      });
      setSize({
        width: previousStateRef.current.width,
        height: previousStateRef.current.height,
      });
      setIsMaximized(false);
    } else {
      previousStateRef.current = {
        x: position.x,
        y: position.y,
        width: size.width,
        height: size.height,
      };
      // Position below menu bar (or top of screen if menu bar is hidden)
      setPosition({ x: 0, y: effectiveMenuBarHeight });
      setSize({
        width: window.innerWidth,
        height: window.innerHeight - effectiveMenuBarHeight,
      });
      setIsMaximized(true);
    }

    setTimeout(() => setIsTransitioning(false), 200);
  }, [canMaximize, isMaximized, position, size, effectiveMenuBarHeight]);

  // Re-flow windows configured with `dimensionPercentage` as the viewport
  // resizes — until the user resizes/maximizes (then `userResizedRef` locks
  // it). Skipped on mobile (auto-maximized), and skipped while maximized or
  // snapped (their own effect below already handles those).
  useEffect(() => {
    if (!configDimensionPercentage) return;
    if (isMobile) return;
    if (!isOpen) return;
    if (isMaximized || isSnapped) return;

    let rafId = 0;

    const apply = () => {
      if (userResizedRef.current) return;
      const availW = window.innerWidth;
      const availH = Math.max(
        0,
        (window.visualViewport?.height ?? window.innerHeight) -
          effectiveMenuBarHeight -
          dockHeight
      );
      const px = resolveDimensionPercentage(
        configDimensionPercentage,
        availW,
        availH
      );
      if (!px) return;
      // Suppress geometry persistence for this viewport-driven update —
      // it's not a user gesture, so we don't want it written to storage.
      viewportResizingRef.current = true;
      // Update size from the percentage and clamp position so the window
      // stays on screen. Position itself is left alone (don't fight the
      // user's dragged placement).
      const clamped = clampToViewport(
        latestPositionRef.current.x,
        latestPositionRef.current.y,
        Math.max(minWidth, px.width),
        Math.max(minHeight, px.height),
        minWidth,
        minHeight,
        dockHeight
      );
      setSize({ width: clamped.width, height: clamped.height });
      setPosition({ x: clamped.x, y: clamped.y });
      window.setTimeout(() => {
        viewportResizingRef.current = false;
      }, 200);
    };

    const onResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(apply);
    };

    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
    };
  }, [
    configDimensionPercentage,
    isMobile,
    isOpen,
    isMaximized,
    isSnapped,
    effectiveMenuBarHeight,
    dockHeight,
    minWidth,
    minHeight,
  ]);

  // Update maximized/snapped windows when viewport resizes or menu bar visibility changes.
  // On touch devices, visualViewport.height reflects the area above the keyboard, so
  // the window shrinks to fit the visible area. The Desktop container stays full-screen
  // behind the window to prevent any background gap showing through.
  const viewportResizingRef = useRef(false);
  useEffect(() => {
    if (!isMaximized && !isSnapped) return;

    let rafId = 0;
    let settleTimer = 0;

    const applyLayout = () => {
      const vw = window.innerWidth;
      const vh = window.visualViewport?.height ?? window.innerHeight;

      if (isMaximized) {
        setPosition({ x: 0, y: effectiveMenuBarHeight });
        setSize({ width: vw, height: vh - effectiveMenuBarHeight });
      } else if (isSnapped === "left") {
        setPosition({ x: 0, y: effectiveMenuBarHeight });
        setSize({ width: vw / 2, height: vh - effectiveMenuBarHeight });
      } else if (isSnapped === "right") {
        setPosition({ x: vw / 2, y: effectiveMenuBarHeight });
        setSize({ width: vw / 2, height: vh - effectiveMenuBarHeight });
      }
    };

    const onViewportResize = () => {
      // Mark as actively resizing to suppress geometry persistence
      viewportResizingRef.current = true;
      clearTimeout(settleTimer);

      // Coalesce rapid resize events into a single rAF
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        applyLayout();

        // Clear the resizing flag after events settle (200ms debounce)
        settleTimer = window.setTimeout(() => {
          viewportResizingRef.current = false;
        }, 200);
      });
    };

    // Apply immediately when effectiveMenuBarHeight changes (not a zoom event)
    applyLayout();

    const vv = window.visualViewport;
    window.addEventListener("resize", onViewportResize);
    vv?.addEventListener("resize", onViewportResize);
    // Only listen to vv scroll when keyboard is NOT open — otherwise it
    // creates a reposition loop that blocks content scrolling on iPad.
    if (!keyboardOpen) {
      vv?.addEventListener("scroll", onViewportResize);
    }
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(settleTimer);
      viewportResizingRef.current = false;
      window.removeEventListener("resize", onViewportResize);
      vv?.removeEventListener("resize", onViewportResize);
      vv?.removeEventListener("scroll", onViewportResize);
    };
  }, [isMaximized, isSnapped, effectiveMenuBarHeight, keyboardOpen]);

  const contentRef = useRef<HTMLDivElement | null>(null);

  /* ========================================================================
     DRAG & DROP HANDLERS (inter-window file drop)
     ======================================================================== */

  // When an internal file-move drag is active, step fully out of the
  // way so the app's own drop targets can handle dragover/drop without
  // the window-level fallback competing.
  const handleContentDragOver = useCallback((e: React.DragEvent) => {
    if (getActiveDragPayload()) return;
    if (e.dataTransfer.types.includes("application/x-desktop-file")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const handleContentDragEnter = useCallback((e: React.DragEvent) => {
    if (getActiveDragPayload()) return;
    if (e.dataTransfer.types.includes("application/x-desktop-file")) {
      e.preventDefault();
      dragOverCountRef.current++;
      setIsDragOver(true);
    }
  }, []);

  const handleContentDragLeave = useCallback(() => {
    if (getActiveDragPayload()) return;
    dragOverCountRef.current--;
    if (dragOverCountRef.current <= 0) {
      dragOverCountRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const handleContentDrop = useCallback((e: React.DragEvent) => {
    if (getActiveDragPayload()) return;
    e.preventDefault();
    dragOverCountRef.current = 0;
    setIsDragOver(false);

    const fileData = e.dataTransfer.getData("application/x-desktop-file");
    if (!fileData) return;

    try {
      const { path } = JSON.parse(fileData) as { path: string; name: string };
      // Dispatch the same event Scout uses on double-click
      window.dispatchEvent(
        new CustomEvent("scout:open-file", { detail: { path } })
      );
    } catch {
      // Invalid data, ignore
    }
  }, []);

  const handleReload = useCallback(() => {
    if (isBrowserWindow(config)) {
      setIframeLoading(true);
      setIframeError(null);
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.location.reload();
      }
    }
  }, [config]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoading(false);
    setIframeError(null);
  }, []);

  const handleIframeError = useCallback(() => {
    setIframeLoading(false);
    setIframeError("Failed to load content. The site may block embedding.");
  }, []);

  /* ========================================================================
     OPENING ANIMATION
     ======================================================================== */

  useEffect(() => {
    if (isOpening && hasIconAnimation) {
      setIsTransitioning(true);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const newPos = { x: defaultX, y: defaultY };
          const newSize = {
            width: parseSize(defaultWidth),
            height: parseSize(defaultHeight),
          };
          setPosition(newPos);
          setSize(newSize);
          setOpacity(1);

          setTimeout(() => {
            setIsTransitioning(false);
            setIsOpening(false);
            savedPosition.current = newPos;
            savedSize.current = newSize;
          }, 300);
        });
      });
    }
  }, [
    isOpening,
    hasIconAnimation,
    defaultX,
    defaultY,
    defaultWidth,
    defaultHeight,
  ]);

  /* ========================================================================
     RESTORE FROM MINIMIZE
     ======================================================================== */

  useEffect(() => {
    // Fire when we transition out of the minimized state AND the minimize
    // animation previously completed (flag set in handleMinimize's
    // setTimeout). We can't use isClosing here — it gets cleared eagerly
    // so that hiddenMounted can engage for the App Switcher preview.
    if (
      isOpen &&
      !isMinimized &&
      needsRestoreAnimation.current &&
      !isAnimating.current
    ) {
      needsRestoreAnimation.current = false;
      isAnimating.current = true;
      setIsTransitioning(true);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPosition(savedPosition.current);
          setSize(savedSize.current);
          setOpacity(1);

          setTimeout(() => {
            setIsTransitioning(false);
            isAnimating.current = false;
          }, 300);
        });
      });
    }
  }, [isOpen, isMinimized]);

  // Keep WM isMaximized in sync with local state.
  // Local isMaximized can change from many paths (maximize button, drag-to-snap,
  // drag-out-of-maximize, restore-from-minimize) so a single sync effect is
  // simpler and more reliable than calling actions in every path.
  useEffect(() => {
    if (!isOpen) return;
    if (isWindowOpen(state) && state.isMaximized !== isMaximized) {
      if (isMaximized) {
        actions.maximize();
      } else {
        actions.unmaximize();
      }
    }
  }, [isMaximized, isOpen, state, actions]);

  /* ========================================================================
     DRAG HANDLERS
     ======================================================================== */

  const handleDragPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isActive) {
        actions.focus();
      }

      e.preventDefault();
      // Blur focused input/textarea to dismiss the caret — on iPadOS Safari
      // the caret doesn't follow when the window moves via CSS positioning.
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setShowTrafficOverlay(false);
      dragRef.current = {
        startX: e.clientX - position.x,
        startY: e.clientY - position.y,
        needsRestore: isMaximized || isSnapped !== null,
        initialClientX: e.clientX,
        initialClientY: e.clientY,
        startTime: Date.now(),
      };
      setIsDragging(true);
    },
    [actions, isActive, isMaximized, isSnapped, position]
  );

  const handleTitleBarDoubleClick = useCallback(() => {
    if (!isMobile && canMaximize) {
      handleMaximize();
    }
  }, [isMobile, canMaximize, handleMaximize]);

  /* ========================================================================
     DRAG EFFECT
     ======================================================================== */

  useEffect(() => {
    if (!isDragging) return;

    // Notify drag-close target that a window drag started (mobile only)
    if (isMobile) {
      window.dispatchEvent(
        new CustomEvent("window:drag-start", {
          detail: { windowId: config.id },
        })
      );
    }

    const SNAP_THRESHOLD = 10;
    const DRAG_THRESHOLD = 1;

    const handlePointerMove = (e: PointerEvent) => {
      // Handle restore from maximized/snapped
      if (dragRef.current.needsRestore) {
        const deltaX = Math.abs(e.clientX - dragRef.current.initialClientX);
        const deltaY = Math.abs(e.clientY - dragRef.current.initialClientY);

        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          const prevWidth = previousStateRef.current.width;
          const prevHeight = previousStateRef.current.height;
          const cursorRatioX = e.clientX / window.innerWidth;
          const newX = e.clientX - prevWidth * cursorRatioX;
          const newY = e.clientY - 24;

          setIsTransitioning(true);
          setPosition({ x: Math.max(0, newX), y: Math.max(0, newY) });
          setSize({ width: prevWidth, height: prevHeight });
          setIsMaximized(false);
          setIsSnapped(null);
          setTimeout(() => setIsTransitioning(false), 150);

          dragRef.current = {
            startX: e.clientX - Math.max(0, newX),
            startY: e.clientY - Math.max(0, newY),
            needsRestore: false,
            initialClientX: e.clientX,
            initialClientY: e.clientY,
            startTime: dragRef.current.startTime,
          };
          return;
        }
        return;
      }

      const newX = e.clientX - dragRef.current.startX;
      const newY = Math.max(0, e.clientY - dragRef.current.startY);
      setPosition({ x: newX, y: newY });

      // Notify drag-close target of cursor position (mobile only)
      if (isMobile) {
        window.dispatchEvent(
          new CustomEvent("window:drag-move", {
            detail: {
              windowId: config.id,
              clientX: e.clientX,
              clientY: e.clientY,
            },
          })
        );
      }

      // Detect snap zones
      if (e.clientY <= SNAP_THRESHOLD) {
        setSnapZone("top");
      } else if (e.clientX <= SNAP_THRESHOLD) {
        setSnapZone("left");
      } else if (e.clientX >= window.innerWidth - SNAP_THRESHOLD) {
        setSnapZone("right");
      } else {
        setSnapZone(null);
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      // Notify drag-close target that drag ended (mobile only)
      if (isMobile) {
        window.dispatchEvent(
          new CustomEvent("window:drag-end", {
            detail: {
              windowId: config.id,
              clientX: e.clientX,
              clientY: e.clientY,
            },
          })
        );
      }

      // If drag-close was triggered (synchronously via the event above),
      // skip all position saving so the window reopens cleanly
      if (dragClosedRef.current) {
        setIsDragging(false);
        return;
      }

      // Detect tap on mobile: minimal movement + short duration
      if (isMobile) {
        const dx = Math.abs(e.clientX - dragRef.current.initialClientX);
        const dy = Math.abs(e.clientY - dragRef.current.initialClientY);
        const elapsed = Date.now() - dragRef.current.startTime;
        if (dx < 8 && dy < 8 && elapsed < 300) {
          // Check if the tap was near the stoplight buttons — if so, fire
          // the closest button action directly instead of showing the overlay
          if (stoplightRef.current) {
            const rect = stoplightRef.current.getBoundingClientRect();
            const PAD = 12; // extra padding around the stoplight area
            if (
              e.clientX >= rect.left - PAD &&
              e.clientX <= rect.right + PAD &&
              e.clientY >= rect.top - PAD &&
              e.clientY <= rect.bottom + PAD
            ) {
              // Determine which button based on horizontal thirds
              const relX = e.clientX - rect.left;
              const third = rect.width / 3;
              if (relX < third) {
                actions.close();
              } else if (relX < third * 2) {
                if (canMinimize) handleMinimize();
              } else {
                if (canMaximize) handleMaximize();
              }
              setIsDragging(false);
              return;
            }
          }

          setShowTrafficOverlay(true);
          setIsDragging(false);
          return;
        }
      }

      dragRef.current.needsRestore = false;

      if (snapZone) {
        setIsTransitioning(true);

        if (!isMaximized && !isSnapped) {
          previousStateRef.current = {
            x: savedPosition.current.x,
            y: savedPosition.current.y,
            width: savedSize.current.width,
            height: savedSize.current.height,
          };
        }

        if (snapZone === "top") {
          setPosition({ x: 0, y: effectiveMenuBarHeight });
          setSize({
            width: window.innerWidth,
            height: window.innerHeight - effectiveMenuBarHeight,
          });
          setIsMaximized(true);
          setIsSnapped(null);
        } else if (snapZone === "left") {
          setPosition({ x: 0, y: effectiveMenuBarHeight });
          setSize({
            width: window.innerWidth / 2,
            height: window.innerHeight - effectiveMenuBarHeight,
          });
          setIsSnapped("left");
          setIsMaximized(false);
        } else if (snapZone === "right") {
          setPosition({ x: window.innerWidth / 2, y: effectiveMenuBarHeight });
          setSize({
            width: window.innerWidth / 2,
            height: window.innerHeight - effectiveMenuBarHeight,
          });
          setIsSnapped("right");
          setIsMaximized(false);
        }

        setTimeout(() => setIsTransitioning(false), 200);
        setSnapZone(null);
      } else if (!isMaximized && !isSnapped) {
        savedPosition.current = { x: position.x, y: position.y };
        savedSize.current = { width: size.width, height: size.height };
      }

      setIsDragging(false);
    };

    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging, isMobile, position, size, snapZone, isMaximized, isSnapped]);

  /* ========================================================================
     RESIZE HANDLERS
     ======================================================================== */

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent, direction: ResizeDirection) => {
      if (!canResize) return;

      if (!isActive) {
        actions.focus();
      }

      e.preventDefault();
      e.stopPropagation();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      userResizedRef.current = true;
      setIsResizing(direction);
      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startWidth: size.width,
        startHeight: size.height,
        startPosX: position.x,
        startPosY: position.y,
      };
    },
    [actions, canResize, isActive, position, size]
  );

  useEffect(() => {
    if (!isResizing) return;

    const handlePointerMove = (e: PointerEvent) => {
      const deltaX = e.clientX - resizeRef.current.startX;
      const deltaY = e.clientY - resizeRef.current.startY;

      let newWidth = resizeRef.current.startWidth;
      let newHeight = resizeRef.current.startHeight;
      let newX = resizeRef.current.startPosX;
      let newY = resizeRef.current.startPosY;

      if (isResizing.includes("right")) {
        newWidth = Math.max(minWidth, resizeRef.current.startWidth + deltaX);
      }
      if (isResizing.includes("left")) {
        const potentialWidth = resizeRef.current.startWidth - deltaX;
        if (potentialWidth >= minWidth) {
          newWidth = potentialWidth;
          newX = resizeRef.current.startPosX + deltaX;
        }
      }
      if (isResizing.includes("bottom")) {
        newHeight = Math.max(minHeight, resizeRef.current.startHeight + deltaY);
      }
      if (isResizing.includes("top")) {
        const potentialHeight = resizeRef.current.startHeight - deltaY;
        const potentialY = resizeRef.current.startPosY + deltaY;
        if (potentialHeight >= minHeight && potentialY >= 0) {
          newHeight = potentialHeight;
          newY = potentialY;
        }
      }

      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = () => {
      setIsResizing(null);
      savedPosition.current = { x: position.x, y: position.y };
      savedSize.current = { width: size.width, height: size.height };
    };

    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isResizing, minWidth, minHeight, position, size]);

  /* ========================================================================
     WINDOW ACTIVATION
     ======================================================================== */

  const handleWindowPointerDown = useCallback(() => {
    if (!isActive) {
      actions.focus();
    }
  }, [actions, isActive]);

  /* ========================================================================
     SNAP ZONE PREVIEW
     ======================================================================== */

  const snapPreviewStyle = snapZone
    ? {
        top: MENU_BAR_HEIGHT + 8,
        left: snapZone === "right" ? window.innerWidth / 2 + 4 : 8,
        width:
          snapZone === "top"
            ? window.innerWidth - 16
            : window.innerWidth / 2 - 12,
        height: window.innerHeight - MENU_BAR_HEIGHT - 16,
      }
    : null;

  /* ========================================================================
     RENDER
     ======================================================================== */

  // Don't render if window is closed
  if (!isOpen && !isMinimized) {
    return null;
  }

  // Don't render if minimized and animation is complete (unless keepMounted)
  if (isMinimized && !isClosing && !keepMounted) {
    return null;
  }

  const hiddenMounted = keepMounted && isMinimized && !isClosing;

  const isBrowser = isBrowserWindow(config);
  const isApp = isAppWindow(config);

  /* --------------------------------------------------------------------
     PREVIEW MODE (CSS-only)
     --------------------------------------------------------------------
     Earlier attempts used createPortal to reparent the window content
     into an App Switcher slot. Every portal-target flip caused React or
     the browser to drop paint state somewhere along the chain, and any
     conditional-render trick that crossed the boundary of "is AppContent
     rendered inline vs in a portal" remounted the component at the
     worst possible moment.

     CSS-only approach: the Window component is rendered exactly the same
     way in every mode. When we need to show it as a preview, we just
     restyle the outer container with position:fixed at the slot rect and
     a transform:scale that fits the native window dimensions to that
     rect. No DOM moves, no portals, no remounts — the browser composites
     the same painted subtree at a different location and scale. */
  const inPreview = hiddenMounted && !!previewRect;

  // Native dimensions used for preview scaling. Falls back to config-
  // declared sizes because the window's runtime `size` state can be
  // tiny (the minimize animation resizes it down to 60×60 and leaves it
  // there until restore).
  const previewNativeWidth = isMobile
    ? Math.max(window.innerWidth, 1)
    : Math.max(parseSize(width), 1);
  const previewNativeHeight = isMobile
    ? Math.max(window.innerHeight, 1)
    : Math.max(parseSize(height), 1);

  const previewScale = inPreview
    ? Math.min(
        previewRect!.width / previewNativeWidth,
        previewRect!.height / previewNativeHeight
      )
    : 1;
  const previewOffsetX = inPreview
    ? (previewRect!.width - previewNativeWidth * previewScale) / 2
    : 0;
  const previewOffsetY = inPreview
    ? (previewRect!.height - previewNativeHeight * previewScale) / 2
    : 0;

  return (
    <>
      {/* Snap Zone Preview */}
      {!hiddenMounted && snapZone && snapPreviewStyle ? (
        <div
          className="pointer-events-none fixed z-[100] rounded-xl border-2 border-white/40 bg-white/10 backdrop-blur-sm transition-all duration-150"
          style={{
            top: snapPreviewStyle.top,
            left: snapPreviewStyle.left,
            width: snapPreviewStyle.width,
            height: snapPreviewStyle.height,
          }}
        />
      ) : null}

      {/* Window Container — always rendered when open OR hiddenMounted.
          Styles switch between normal window layout and preview overlay
          based on inPreview. AppContent stays mounted inline the entire
          time; only CSS changes. */}
      <div
        data-hurricane-window
        className={`${inPreview || (hiddenMounted && !previewRect) ? "fixed" : "absolute"} ${
          isTransitioning && !hiddenMounted
            ? isOpening || isClosing
              ? "transition-all duration-300 ease-out"
              : "transition-all duration-200"
            : ""
        } ${!isMaximized && !isMobile && !inPreview ? "rounded-2xl" : ""}`}
        style={
          hiddenMounted && !previewRect
            ? {
                // Minimized but switcher not open — hide window entirely.
                bottom: 0,
                left: 0,
                width: 1,
                height: 1,
                overflow: "hidden",
                visibility: "hidden",
                pointerEvents: "none",
                zIndex: -1,
              }
            : inPreview
              ? {
                  // Minimized WITH switcher open — position+scale over slot.
                  top: previewRect!.y,
                  left: previewRect!.x,
                  width: `${previewNativeWidth}px`,
                  height: `${previewNativeHeight}px`,
                  transform: `translate(${previewOffsetX}px, ${previewOffsetY}px) scale(${previewScale})`,
                  transformOrigin: "top left",
                  overflow: "hidden",
                  borderRadius: "12px",
                  opacity: 1,
                  zIndex: 221,
                  pointerEvents: "none",
                  boxShadow: "none",
                }
              : {
                  // Normal open-window styling.
                  transform: `translate(${position.x}px, ${position.y}px)`,
                  width: `${size.width}px`,
                  height: `${size.height}px`,
                  opacity: opacity,
                  zIndex: isActive ? 10 + zIndex : zIndex,
                  boxShadow:
                    "0 25px 80px -12px rgba(0, 0, 0, 0.8), 0 12px 40px -8px rgba(0, 0, 0, 0.6)",
                  pointerEvents: isMinimized ? "none" : "auto",
                }
        }
        onPointerDown={handleWindowPointerDown}
      >
        {/* Inner Container */}
        <div
          className={`relative h-full w-full overflow-hidden backdrop-blur-xl ${
            !isMaximized && !isMobile ? "rounded-2xl" : ""
          }`}
          style={{
            backgroundColor: colors.windowBg,
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: colors.border,
          }}
        >
          {/* Title Bar */}
          {!hideTitleBar && (
            <div
              data-window-titlebar
              className={`relative flex h-12 select-none items-center px-4 transition-colors ${
                isMobile ? "cursor-default" : "cursor-move"
              } ${!isMaximized && !isMobile ? "rounded-t-2xl" : ""}`}
              style={{
                touchAction: "none",
                backgroundColor: isActive
                  ? colors.titleBarBg
                  : colors.titleBarBgInactive,
                borderBottomWidth: "1px",
                borderBottomStyle: "solid",
                borderBottomColor: colors.border,
              }}
              onPointerDown={handleDragPointerDown}
              onDoubleClick={handleTitleBarDoubleClick}
            >
              {/* Enlarged Traffic Light Overlay (mobile tap) */}
              {showTrafficOverlay && (
                <>
                  {/* Invisible backdrop to catch taps outside */}
                  <div
                    className="fixed inset-0"
                    style={{ zIndex: 10001 }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setShowTrafficOverlay(false);
                    }}
                  />
                  {/* Overlay bubble */}
                  <div
                    className="absolute left-2 top-1/2 flex -translate-y-1/2 items-center gap-4 rounded-2xl px-5 py-3"
                    style={{
                      zIndex: 10002,
                      backgroundColor: isActive
                        ? colors.titleBarBg
                        : colors.titleBarBgInactive,
                      boxShadow:
                        "0 8px 32px rgba(0, 0, 0, 0.35), 0 2px 8px rgba(0, 0, 0, 0.2)",
                      border: `1px solid ${colors.border}`,
                      backdropFilter: "blur(20px)",
                      animation: "trafficOverlayIn 0.2s ease-out",
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => {
                        setShowTrafficOverlay(false);
                        handleClose();
                      }}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500 transition-transform active:scale-90"
                      aria-label="Close"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="rgba(0,0,0,0.5)"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <line x1="3" y1="3" x2="11" y2="11" />
                        <line x1="11" y1="3" x2="3" y2="11" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        setShowTrafficOverlay(false);
                        if (canMinimize) handleMinimize();
                      }}
                      disabled={!canMinimize}
                      className={`flex h-9 w-9 items-center justify-center rounded-full transition-transform active:scale-90 ${
                        !canMinimize
                          ? "cursor-not-allowed bg-zinc-600"
                          : "bg-yellow-500"
                      }`}
                      aria-label="Minimize"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="rgba(0,0,0,0.5)"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <line x1="3" y1="7" x2="11" y2="7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        setShowTrafficOverlay(false);
                        if (canMaximize) handleMaximize();
                      }}
                      disabled={!canMaximize}
                      className={`flex h-9 w-9 items-center justify-center rounded-full transition-transform active:scale-90 ${
                        !canMaximize
                          ? "cursor-not-allowed bg-zinc-600"
                          : "bg-green-500"
                      }`}
                      aria-label="Maximize"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="rgba(0,0,0,0.5)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {isMaximized ? (
                          <>
                            <polyline points="4 9 4 11 6 11" />
                            <polyline points="10 5 10 3 8 3" />
                          </>
                        ) : (
                          <>
                            <polyline points="4 9 4 11 6 11" />
                            <polyline points="10 5 10 3 8 3" />
                            <line x1="3" y1="11" x2="11" y2="3" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                </>
              )}
              {/* Stoplight Buttons */}
              <div
                ref={stoplightRef}
                className="flex items-center gap-2"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <button
                  onClick={handleClose}
                  className={`h-3 w-3 rounded-full transition-colors ${
                    isActive ? "bg-red-500 hover:bg-red-600" : "bg-zinc-600"
                  }`}
                  aria-label="Close"
                />
                <button
                  onClick={canMinimize ? handleMinimize : undefined}
                  disabled={!canMinimize}
                  className={`h-3 w-3 rounded-full transition-colors ${
                    !canMinimize
                      ? "cursor-not-allowed bg-zinc-600"
                      : isActive
                        ? "bg-yellow-500 hover:bg-yellow-600"
                        : "bg-zinc-600"
                  }`}
                  aria-label="Minimize"
                />
                <button
                  onClick={canMaximize ? handleMaximize : undefined}
                  disabled={!canMaximize}
                  className={`h-3 w-3 rounded-full transition-colors ${
                    !canMaximize
                      ? "cursor-not-allowed bg-zinc-600"
                      : isActive
                        ? "bg-green-500 hover:bg-green-600"
                        : "bg-zinc-600"
                  }`}
                  aria-label="Maximize"
                />
              </div>

              {/* Title / URL Bar */}
              {isBrowser ? (
                <div className="ml-3 flex flex-1 items-center gap-2">
                  {(config as BrowserWindowConfig).showReloadButton !==
                    false && (
                    <button
                      onClick={handleReload}
                      onPointerDown={(e) => e.stopPropagation()}
                      className="rounded-md p-1.5 transition-colors"
                      style={{
                        color: colors.textSecondary,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.hoverBg;
                        e.currentTarget.style.color = colors.textPrimary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = colors.textSecondary;
                      }}
                      aria-label="Reload"
                    >
                      <ReloadIcon />
                    </button>
                  )}
                  <div
                    className="flex flex-1 items-center gap-2 rounded-md px-3 py-1.5 text-sm"
                    style={{
                      backgroundColor: colors.urlBarBg,
                      color: colors.textSecondary,
                    }}
                  >
                    <span
                      className={`transition-all ${
                        isActive ? "" : "opacity-50 grayscale"
                      }`}
                    >
                      {config.icon}
                    </span>
                    <span className="truncate">{config.title}</span>
                  </div>
                </div>
              ) : (
                <div
                  className="ml-3 flex flex-1 items-center gap-2 text-sm font-medium"
                  style={{ color: colors.textPrimary }}
                >
                  <span
                    className={`transition-all ${
                      isActive ? "" : "opacity-50 grayscale"
                    }`}
                  >
                    {config.icon}
                  </span>
                  <span>{config.title}</span>
                </div>
              )}

              {/* Share Button */}
              <button
                onClick={handleShare}
                onPointerDown={(e) => e.stopPropagation()}
                className="ml-auto rounded-md p-1.5 transition-colors"
                style={{
                  color: colors.textSecondary,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.hoverBg;
                  e.currentTarget.style.color = colors.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = colors.textSecondary;
                }}
                aria-label="Share"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
              </button>
            </div>
          )}

          {/* Content Area */}
          <div
            ref={contentRef}
            className={`relative select-auto ${
              hideTitleBar ? "h-full" : "h-[calc(100%-3rem)]"
            } overflow-auto`}
            onDragOver={handleContentDragOver}
            onDragEnter={handleContentDragEnter}
            onDragLeave={handleContentDragLeave}
            onDrop={handleContentDrop}
          >
            {/* App Content — rendered inline, always. */}
            {isApp && <AppContent config={config as AppWindowConfig} />}

            {/* Browser Content */}
            {isBrowser && (
              <BrowserContent
                config={config as BrowserWindowConfig}
                iframeRef={iframeRef}
                isLoading={iframeLoading}
                error={iframeError}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                onRetry={handleReload}
                colors={colors}
              />
            )}

            {/* Drop zone overlay — only for genuine external (OS) file
                drags. Internal desktop-file moves set an active payload
                and handle their own drop targets, so skip the overlay
                there to avoid a competing window-wide highlight. */}
            {isDragOver && !getActiveDragPayload() && (
              <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-sm border-2 border-dashed border-blue-400/60 bg-blue-500/15">
                <div className="rounded-lg bg-blue-500/90 px-4 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-sm">
                  Drop to open file
                </div>
              </div>
            )}

            {/* Click overlay when inactive. Goes transparent to pointer
                events while a desktop-file drag is in flight so the drop
                can reach the app's own drop targets underneath. */}
            {!isActive && (
              <div
                className={`absolute inset-0 z-10 ${
                  isFileDragInFlight ? "pointer-events-none" : ""
                }`}
                onPointerDown={handleWindowPointerDown}
              />
            )}
          </div>
        </div>

        {/* Resize Handles */}
        {canResize && !isMaximized && (
          <>
            {/* Corners */}
            <div
              className="absolute left-0 top-0 h-4 w-4 cursor-nwse-resize"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => handleResizePointerDown(e, "top-left")}
            />
            <div
              className="absolute right-0 top-0 h-4 w-4 cursor-nesw-resize"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => handleResizePointerDown(e, "top-right")}
            />
            <div
              className="absolute bottom-0 left-0 h-4 w-4 cursor-nesw-resize"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => handleResizePointerDown(e, "bottom-left")}
            />
            <div
              className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => handleResizePointerDown(e, "bottom-right")}
            />

            {/* Edges */}
            <div
              className="absolute left-4 right-4 top-0 h-2 cursor-ns-resize"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => handleResizePointerDown(e, "top")}
            />
            <div
              className="absolute bottom-0 left-4 right-4 h-2 cursor-ns-resize"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => handleResizePointerDown(e, "bottom")}
            />
            <div
              className="absolute bottom-4 left-0 top-4 w-2 cursor-ew-resize"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => handleResizePointerDown(e, "left")}
            />
            <div
              className="absolute bottom-4 right-0 top-4 w-2 cursor-ew-resize"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => handleResizePointerDown(e, "right")}
            />
          </>
        )}
      </div>
    </>
  );
}

/* ==========================================================================
   SUB-COMPONENTS
   ========================================================================== */

/**
 * Per-window error boundary — catches lazy import failures (and other render
 * errors) so a single broken app doesn't take down the whole desktop.
 */
class WindowErrorBoundary extends Component<
  { children: ReactNode; windowTitle?: string },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isStaleChunkError(error)) {
      handleStaleChunk();
    }

    console.error(
      `[Window: ${this.props.windowTitle ?? "unknown"}] Render error:`,
      error,
      info.componentStack
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <WindowErrorFallback
          error={this.state.error}
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      );
    }
    return this.props.children;
  }
}

function WindowErrorFallback({
  error,
  onRetry,
}: {
  error: Error | null;
  onRetry: () => void;
}) {
  const themeContext = useThemeOptional();
  const colors = themeContext?.colors ?? darkTheme;
  const [retryFailed, setRetryFailed] = useState(false);

  const isChunkError = isStaleChunkError(error);

  useEffect(() => {
    if (isChunkError) {
      handleStaleChunk();
    }
  }, [isChunkError]);

  const handleRetry = () => {
    setRetryFailed(true);
    onRetry();
  };

  const handleRestart = async () => {
    if (isChunkError) {
      handleStaleChunk({ force: true });
      return;
    }

    await hardReloadApp();
  };

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center"
      style={{ color: colors.textPrimary }}
    >
      <div className="text-3xl">⚠️</div>
      <div
        className="text-sm font-semibold"
        style={{ color: colors.textPrimary, opacity: 0.8 }}
      >
        {isChunkError
          ? "App update detected — restarting system"
          : "This app crashed"}
      </div>
      {error?.message && (
        <div
          className="max-w-xs truncate text-xs"
          style={{ color: colors.textSecondary }}
        >
          {error.message}
        </div>
      )}
      <div className="mt-2 flex gap-2">
        {isChunkError ? (
          <button
            onClick={handleRestart}
            className="rounded px-3 py-1.5 text-xs transition-colors"
            style={{
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
            }}
          >
            Restart Now
          </button>
        ) : retryFailed ? (
          <button
            onClick={handleRestart}
            className="rounded px-3 py-1.5 text-xs transition-colors"
            style={{
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
            }}
          >
            Restart
          </button>
        ) : (
          <button
            onClick={handleRetry}
            className="rounded px-3 py-1.5 text-xs transition-colors"
            style={{
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
            }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Renders the component for an app window.
 */
function AppContent({ config }: { config: AppWindowConfig }) {
  const AppComponent = config.component as ComponentType<
    Record<string, unknown>
  >;
  const props = config.componentProps ?? {};

  return (
    <ActivityTrackingProvider appId={config.id}>
      <WindowErrorBoundary windowTitle={config.title}>
        <Suspense fallback={<AppLoadingFallback />}>
          <AppComponent {...props} />
        </Suspense>
      </WindowErrorBoundary>
    </ActivityTrackingProvider>
  );
}

/**
 * Loading fallback shown while a lazy-loaded app component is being fetched.
 */
function AppLoadingFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-current border-t-transparent opacity-40" />
    </div>
  );
}

/**
 * Simple reload icon (inline SVG to avoid external dependencies).
 */
function ReloadIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 2v6h-6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M3 22v-6h6" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
  );
}

/**
 * Browser content with loading and error states.
 */
function BrowserContent({
  config,
  iframeRef,
  isLoading,
  error,
  onLoad,
  onError,
  onRetry,
  colors,
}: {
  config: BrowserWindowConfig;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  isLoading: boolean;
  error: string | null;
  onLoad: () => void;
  onError: () => void;
  onRetry: () => void;
  colors: {
    overlayBg: string;
    textSecondary: string;
    textPrimary: string;
    border: string;
    hoverBg: string;
  };
}) {
  return (
    <div
      className="relative h-full w-full"
      style={{ backgroundColor: colors.overlayBg }}
    >
      {/* Loading Spinner */}
      {isLoading && !error && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          style={{ backgroundColor: colors.overlayBg }}
        >
          <div className="flex flex-col items-center gap-3">
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-t-blue-500"
              style={{ borderColor: colors.border, borderTopColor: "#3b82f6" }}
            />
            <span className="text-sm" style={{ color: colors.textSecondary }}>
              Loading...
            </span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          style={{ backgroundColor: colors.overlayBg }}
        >
          <div className="flex max-w-sm flex-col items-center gap-4 px-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20">
              <svg
                className="h-8 w-8 text-red-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div>
              <h3
                className="mb-1 font-medium"
                style={{ color: colors.textPrimary }}
              >
                Unable to load
              </h3>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                {error}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onRetry}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700"
              >
                Try Again
              </button>
              <a
                href={config.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg px-4 py-2 text-sm transition-colors"
                style={{
                  backgroundColor: colors.hoverBg,
                  color: colors.textPrimary,
                }}
              >
                Open Externally
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Iframe */}
      <iframe
        ref={iframeRef}
        src={config.url}
        className={`h-full w-full border-0 transition-opacity duration-300 ${
          isLoading || error ? "opacity-0" : "opacity-100"
        }`}
        title={config.title}
        sandbox={
          config.sandbox ?? "allow-scripts allow-same-origin allow-popups"
        }
        allow={config.allow}
        referrerPolicy={config.referrerPolicy}
        onLoad={onLoad}
        onError={onError}
      />
    </div>
  );
}
